import { useEffect, useMemo, useState } from "react";
import {
  convertAttendanceToPermit,
  previewConvertAttendanceToPermit,
} from "../../api/permit";
import "./ConvertAttendanceToPermitModal.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function formatHistoryDate(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}

const KIND_LABELS = {
  LATE: "تأخير",
  EARLY: "انصراف مبكر",
};

export default function ConvertAttendanceToPermitModal({
  employeeId,
  date,
  employeeName,
  onClose,
  onConverted,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [selectedKinds, setSelectedKinds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    previewConvertAttendanceToPermit({ employeeId, date })
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        const kinds = (data?.windows || []).map((window) => window.kind);
        setSelectedKinds(kinds.length ? kinds : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setError(getApiErrorMessage(err, "تعذر تحميل رصيد الإذن لهذا اليوم"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, date]);

  const availableKinds = useMemo(() => {
    const fromWindows = (preview?.windows || []).map((window) => window.kind);
    if (fromWindows.length) return fromWindows;
    const kinds = [];
    if (Number(preview?.neededLateHours) > 0) kinds.push("LATE");
    if (Number(preview?.neededEarlyHours) > 0) kinds.push("EARLY");
    return kinds;
  }, [preview]);

  const selectedWindows = useMemo(
    () =>
      (preview?.windows || []).filter((window) =>
        selectedKinds.includes(window.kind),
      ),
    [preview, selectedKinds],
  );

  const selectedHours = selectedWindows.reduce(
    (sum, window) => sum + Number(window.hours || 0),
    0,
  );

  function toggleKind(kind) {
    setSelectedKinds((current) =>
      current.includes(kind)
        ? current.filter((item) => item !== kind)
        : [...current, kind],
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!preview?.canConvert || !selectedKinds.length) return;
    setError("");
    setSubmitting(true);
    try {
      const result = await convertAttendanceToPermit({
        employeeId,
        date,
        kinds: selectedKinds,
        reason: reason.trim() || undefined,
      });
      onConverted?.(result);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحويل اليوم إلى إذن"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="cap-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose?.();
      }}
    >
      <form
        className="cap-modal"
        dir="rtl"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="cap-title">تحويل إلى إذن</h3>
        <p className="cap-subtitle">
          {employeeName ? `${employeeName} · ` : ""}
          {formatHistoryDate(date)}
        </p>

        {loading ? <p className="cap-loading">جاري حساب الرصيد والتأخير...</p> : null}
        {error ? <div className="cap-alert error">{error}</div> : null}

        {!loading && preview ? (
          <>
            <dl className="cap-stats">
              <div>
                <dt>رصيد الإذن المتبقي</dt>
                <dd>
                  {preview.remainingHours} من {preview.maxMonthlyHours} ساعة
                </dd>
              </div>
              <div>
                <dt>التأخير</dt>
                <dd>
                  {preview.lateMinutes || 0} د
                  {preview.neededLateHours
                    ? ` → ${preview.neededLateHours} ساعة`
                    : ""}
                </dd>
              </div>
              <div>
                <dt>الانصراف المبكر</dt>
                <dd>
                  {preview.earlyMinutes || 0} د
                  {preview.neededEarlyHours
                    ? ` → ${preview.neededEarlyHours} ساعة`
                    : ""}
                </dd>
              </div>
            </dl>

            {availableKinds.length > 0 ? (
              <fieldset className="cap-kinds">
                <legend>التحويل</legend>
                {availableKinds.map((kind) => {
                  const window = (preview.windows || []).find(
                    (item) => item.kind === kind,
                  );
                  return (
                    <label key={kind} className="cap-kind">
                      <input
                        type="checkbox"
                        checked={selectedKinds.includes(kind)}
                        onChange={() => toggleKind(kind)}
                      />
                      <span>
                        {KIND_LABELS[kind] || kind}
                        {window
                          ? ` · ${window.startTime}–${window.endTime} (${window.hours} س)`
                          : ""}
                        {window?.partial ? " · في حدود الرصيد" : ""}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <p className="cap-hint">
                لا يوجد تأخير أو انصراف مبكر غير مغطى بإذن في هذا اليوم.
              </p>
            )}

            {preview.partial ? (
              <p className="cap-hint">
                الرصيد لا يكفي لتغطية اليوم بالكامل، وسيُحوَّل المتاح فقط ويبقى
                الباقي جزاءً.
              </p>
            ) : null}

            {selectedHours > 0 ? (
              <p className="cap-hint">
                سيتم خصم {selectedHours} ساعة من رصيد الأذونات الشهري.
              </p>
            ) : null}

            <label className="cap-reason">
              <span>السبب (اختياري)</span>
              <input
                type="text"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={500}
                placeholder="يظهر في سجل الإذن وإشعار الموظف"
              />
            </label>
          </>
        ) : null}

        <div className="cap-actions">
          <button
            type="button"
            className="cap-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            إلغاء
          </button>
          <button
            type="submit"
            className="cap-submit"
            disabled={
              submitting ||
              loading ||
              !preview?.canConvert ||
              selectedKinds.length === 0
            }
          >
            {submitting ? "جاري التحويل..." : "تأكيد التحويل"}
          </button>
        </div>
      </form>
    </div>
  );
}
